import { useState, useEffect } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';
import { getAdminToken } from '@/components/feature/AdminGuard';
import { hashPassword } from '@/lib/hashUtils';

const EDGE_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

interface AdminUser {
  id: string;
  username: string;
  nickname: string;
  role: 'super_admin' | 'admin';
  status: 'active' | 'disabled';
  created_at: string;
  last_login_at: string | null;
}

interface Member {
  id: string;
  username: string;
  nickname: string | null;
  phone: string | null;
}

// ── 支付密码重置弹窗 ──────────────────────────────────────────────────────────
const ResetPayPwdModal = ({ member, onClose }: { member: Member; onClose: () => void }) => {
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!/^\d{6}$/.test(newPwd)) { setError('支付密码必须是6位数字'); return; }
    if (newPwd !== confirmPwd) { setError('两次输入不一致'); return; }
    setSubmitting(true); setError('');
    try {
      const adminToken = localStorage.getItem('admin_token') ?? '';
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_reset_pay_password', token: adminToken, userId: member.id, newPassword: newPwd }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error ?? '重置失败，请重试');
        setSubmitting(false);
        return;
      }
      setSuccess(true);
    } catch {
      setError('网络错误，请重试');
    }
    setSubmitting(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !submitting && onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={e => e.stopPropagation()}>
          {success ? (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mb-3">
                <i className="ri-shield-check-line text-emerald-500 text-3xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-1">支付密码重置成功</h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                <span className="font-medium text-gray-700">{member.nickname ?? member.username}</span> 的支付密码已更新
              </p>
              <button onClick={onClose} className="w-full bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap">完成</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-keyhole-line text-violet-500 text-xl" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">重置支付密码</h3>
                  <p className="text-xs text-gray-400">{member.nickname ?? member.username}{member.phone ? ` · ${member.phone}` : ''}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">新支付密码（6位数字）</label>
                  <input type="password" value={newPwd} onChange={e => { setNewPwd(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                    placeholder="请输入6位数字" inputMode="numeric" maxLength={6}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-violet-400 tracking-widest" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">确认新密码</label>
                  <input type="password" value={confirmPwd} onChange={e => { setConfirmPwd(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                    placeholder="再次输入6位数字" inputMode="numeric" maxLength={6}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-violet-400 tracking-widest" />
                </div>
              </div>
              {error && <div className="mt-3 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl"><i className="ri-error-warning-line flex-shrink-0" />{error}</div>}
              <div className="flex gap-3 mt-5">
                <button onClick={onClose} disabled={submitting} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50">取消</button>
                <button onClick={handleSubmit} disabled={submitting || newPwd.length !== 6} className="flex-1 bg-violet-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-violet-600 disabled:opacity-50">
                  {submitting ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />重置中...</span> : '确认重置'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ── 添加/编辑管理员弹窗 ───────────────────────────────────────────────────────
interface AdminFormModalProps {
  editTarget?: AdminUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AdminFormModal = ({ editTarget, onClose, onSuccess }: AdminFormModalProps) => {
  const isEdit = !!editTarget;
  const [username, setUsername] = useState(editTarget?.username ?? '');
  const [nickname, setNickname] = useState(editTarget?.nickname ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'super_admin'>(editTarget?.role ?? 'admin');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!nickname.trim()) { setError('请输入昵称'); return; }
    if (!isEdit && !username.trim()) { setError('请输入用户名'); return; }
    if (!isEdit && password.length < 6) { setError('密码至少6位'); return; }
    if (isEdit && password && password.length < 6) { setError('密码至少6位'); return; }
    setSubmitting(true);
    const token = getAdminToken();
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit
          ? { action: 'admin_update_admin', token, adminId: editTarget!.id, nickname: nickname.trim(), role, ...(password ? { password } : {}) }
          : { action: 'admin_create_admin', token, username: username.trim(), nickname: nickname.trim(), password, role }
        ),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      onSuccess();
    } catch { setError('网络错误，请重试'); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !submitting && onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-admin-line text-indigo-500 text-xl" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">{isEdit ? '编辑管理员' : '添加管理员'}</h3>
              <p className="text-xs text-gray-400">{isEdit ? `@${editTarget!.username}` : '创建新的后台管理账号'}</p>
            </div>
          </div>
          <div className="space-y-3">
            {!isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">用户名 <span className="text-rose-500">*</span></label>
                <input type="text" value={username} onChange={e => { setUsername(e.target.value); setError(''); }}
                  placeholder="登录用，创建后不可修改"
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-400" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">昵称 <span className="text-rose-500">*</span></label>
              <input type="text" value={nickname} onChange={e => { setNickname(e.target.value); setError(''); }}
                placeholder="显示名称"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                密码 {isEdit ? <span className="text-gray-400 font-normal">（留空则不修改）</span> : <span className="text-rose-500">*</span>}
              </label>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder={isEdit ? '留空则不修改密码' : '至少6位'}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none focus:border-indigo-400" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                  <i className={`${showPwd ? 'ri-eye-line' : 'ri-eye-off-line'} text-base`} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">权限角色</label>
              <div className="flex gap-2">
                {(['admin', 'super_admin'] as const).map(r => (
                  <button key={r} type="button" onClick={() => setRole(r)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${role === r ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}>
                    {r === 'super_admin' ? '超级管理员' : '普通管理员'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">超级管理员可管理其他管理员账号</p>
            </div>
          </div>
          {error && <div className="mt-3 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl"><i className="ri-error-warning-line flex-shrink-0" />{error}</div>}
          <div className="flex gap-3 mt-5">
            <button onClick={onClose} disabled={submitting} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50">取消</button>
            <button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-indigo-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-indigo-600 disabled:opacity-50">
              {submitting ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{isEdit ? '保存中...' : '创建中...'}</span> : isEdit ? '保存修改' : '创建账号'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ── 设置导航项 ────────────────────────────────────────────────────────────────
type SettingTab = 'admins' | 'service_phone' | 'pay_password';

const settingNavItems: { id: SettingTab; icon: string; label: string; desc: string }[] = [
  { id: 'admins', icon: 'ri-admin-line', label: '管理员账号', desc: '管理后台登录账号' },
  { id: 'service_phone', icon: 'ri-customer-service-2-line', label: '客服电话', desc: '前台客服联系方式' },
  { id: 'pay_password', icon: 'ri-shield-keyhole-line', label: '支付密码重置', desc: '重置会员支付密码' },
];

// ── 主页面 ────────────────────────────────────────────────────────────────────
const AdminSettingsPage = () => {
  const [activeTab, setActiveTab] = useState<SettingTab>('admins');

  // 客服电话
  const [servicePhone, setServicePhone] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState('');

  // 支付密码重置
  const [members, setMembers] = useState<Member[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState<Member | null>(null);

  // 管理员账号管理
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editAdmin, setEditAdmin] = useState<AdminUser | null>(null);
  const [deleteAdminTarget, setDeleteAdminTarget] = useState<AdminUser | null>(null);
  const [deleteAdminLoading, setDeleteAdminLoading] = useState(false);
  const [currentAdminRole, setCurrentAdminRole] = useState<string>('');

  useEffect(() => {
    const token = getAdminToken();
    if (token) {
      try {
        const [payloadB64] = token.split('.');
        // 支持中文的 base64 解码
        const jsonStr = decodeURIComponent(atob(payloadB64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        const payload = JSON.parse(jsonStr);
        setCurrentAdminRole(payload.adminRole ?? '');
      } catch { /* ignore */ }
    }
  }, []);

  const loadAdminUsers = async () => {
    setAdminLoading(true); setAdminError('');
    const token = getAdminToken();
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_list_admins', token }),
      });
      const data = await res.json();
      if (data.error) setAdminError(data.error);
      else setAdminUsers(data.data ?? []);
    } catch { setAdminError('加载失败，请重试'); }
    finally { setAdminLoading(false); }
  };

  useEffect(() => { loadAdminUsers(); }, []);

  const handleDeleteAdmin = async () => {
    if (!deleteAdminTarget) return;
    setDeleteAdminLoading(true);
    const token = getAdminToken();
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_delete_admin', token, adminId: deleteAdminTarget.id }),
      });
      const data = await res.json();
      if (data.error) setAdminError(data.error);
      else { setDeleteAdminTarget(null); loadAdminUsers(); }
    } catch { setAdminError('删除失败，请重试'); }
    finally { setDeleteAdminLoading(false); }
  };

  useEffect(() => {
    supabase.from('admin_settings').select('value').eq('key', 'customer_service_phone').maybeSingle()
      .then(({ data }) => { const v = data?.value ?? ''; setServicePhone(v); setPhoneInput(v); });
  }, []);

  const savePhone = async () => {
    setPhoneSaving(true); setPhoneMsg('');
    const { error } = await supabase.from('admin_settings')
      .upsert({ key: 'customer_service_phone', value: phoneInput.trim(), updated_at: new Date().toISOString() });
    setPhoneSaving(false);
    if (error) { setPhoneMsg('保存失败，请重试'); return; }
    setServicePhone(phoneInput.trim());
    setPhoneMsg('保存成功！');
    setTimeout(() => setPhoneMsg(''), 3000);
  };

  const searchMembers = async () => {
    if (!memberSearch.trim()) return;
    setMembersLoading(true);
    const q = memberSearch.trim();
    const { data } = await supabase.from('custom_users').select('id, username, nickname, phone')
      .or(`nickname.ilike.%${q}%,username.ilike.%${q}%,phone.ilike.%${q}%`).limit(10);
    setMembers((data as Member[]) ?? []);
    setMembersLoading(false);
  };

  return (
    <AdminLayout title="系统设置">
      <div className="flex gap-6 h-full">

        {/* 左侧二级导航 */}
        <aside className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="px-4 py-3.5 border-b border-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">设置分类</p>
            </div>
            <nav className="p-2 space-y-0.5">
              {settingNavItems.map(item => {
                const active = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all text-left ${active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium whitespace-nowrap">{item.label}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* 右侧内容区 */}
        <div className="flex-1 min-w-0">

          {/* 管理员账号 */}
          {activeTab === 'admins' && (
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-admin-line text-indigo-500 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">管理员账号</h3>
                    <p className="text-xs text-gray-400 mt-0.5">管理可登录后台的管理员账号</p>
                  </div>
                </div>
                {currentAdminRole === 'super_admin' && (
                  <button
                    onClick={() => { setEditAdmin(null); setShowAdminForm(true); }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-indigo-600 transition-colors"
                  >
                    <i className="ri-add-line" />添加管理员
                  </button>
                )}
              </div>

              {adminError && (
                <div className="flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-4 py-3 rounded-xl mb-4">
                  <i className="ri-error-warning-line flex-shrink-0" />{adminError}
                </div>
              )}

              {adminLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  {adminUsers.map((admin, i) => (
                    <div key={admin.id} className={`flex items-center justify-between px-4 py-4 ${i < adminUsers.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${admin.role === 'super_admin' ? 'bg-amber-100' : 'bg-indigo-50'}`}>
                          <i className={`${admin.role === 'super_admin' ? 'ri-vip-crown-line text-amber-500' : 'ri-user-settings-line text-indigo-400'} text-lg`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-800">{admin.nickname}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${admin.role === 'super_admin' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-500'}`}>
                              {admin.role === 'super_admin' ? '超级管理员' : '管理员'}
                            </span>
                            {admin.status === 'disabled' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">已禁用</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            @{admin.username}
                            {admin.last_login_at && (
                              <span className="ml-2">最近登录：{new Date(admin.last_login_at).toLocaleDateString('zh-CN')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      {currentAdminRole === 'super_admin' && (
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          <button onClick={() => { setEditAdmin(admin); setShowAdminForm(true); }}
                            className="text-xs text-indigo-500 border border-indigo-200 px-3 py-1.5 rounded-full cursor-pointer hover:bg-indigo-50 transition-colors whitespace-nowrap">
                            <i className="ri-edit-line mr-1" />编辑
                          </button>
                          <button onClick={() => setDeleteAdminTarget(admin)}
                            className="text-xs text-rose-500 border border-rose-200 px-3 py-1.5 rounded-full cursor-pointer hover:bg-rose-50 transition-colors whitespace-nowrap">
                            <i className="ri-delete-bin-line mr-1" />删除
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {adminUsers.length === 0 && (
                    <div className="text-center py-12 text-gray-400 text-sm">
                      <i className="ri-admin-line text-3xl block mb-2" />暂无管理员账号
                    </div>
                  )}
                </div>
              )}
              {currentAdminRole !== 'super_admin' && (
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                  <i className="ri-information-line" />仅超级管理员可管理账号
                </p>
              )}
            </div>
          )}

          {/* 客服电话 */}
          {activeTab === 'service_phone' && (
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-rose-500 text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">客服电话设置</h3>
                  <p className="text-xs text-gray-400 mt-0.5">设置后将显示在前台客服页面，用户可一键拨打</p>
                </div>
              </div>
              <div className="space-y-3 max-w-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">客服手机号</label>
                  <div className="flex gap-3">
                    <input type="tel" value={phoneInput} onChange={e => setPhoneInput(e.target.value)}
                      placeholder="请输入客服手机号，如：13800138000" maxLength={20}
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-400"
                      onKeyDown={e => e.key === 'Enter' && savePhone()} />
                    <button onClick={savePhone} disabled={phoneSaving || phoneInput.trim() === servicePhone}
                      className="px-6 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors disabled:opacity-50">
                      {phoneSaving ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
                {servicePhone && (
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 text-sm px-4 py-2.5 rounded-xl">
                    <i className="ri-checkbox-circle-line flex-shrink-0" />
                    当前客服电话：<span className="font-semibold">{servicePhone}</span>
                  </div>
                )}
                {phoneMsg && (
                  <div className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl ${phoneMsg.includes('失败') ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    <i className={phoneMsg.includes('失败') ? 'ri-error-warning-line' : 'ri-check-line'} />
                    {phoneMsg}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 支付密码重置 */}
          {activeTab === 'pay_password' && (
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-keyhole-line text-violet-500 text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">重置会员支付密码</h3>
                  <p className="text-xs text-gray-400 mt-0.5">搜索会员后可为其重置积分支付密码</p>
                </div>
              </div>
              <div className="flex gap-3 mb-4 max-w-lg">
                <input type="text" value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                  placeholder="搜索昵称 / 用户名 / 手机号"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-violet-400"
                  onKeyDown={e => e.key === 'Enter' && searchMembers()} />
                <button onClick={searchMembers} disabled={membersLoading || !memberSearch.trim()}
                  className="px-6 py-2.5 bg-violet-500 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-violet-600 transition-colors disabled:opacity-50">
                  {membersLoading ? '搜索中...' : '搜索'}
                </button>
              </div>
              {members.length > 0 && (
                <div className="border border-gray-100 rounded-xl overflow-hidden max-w-lg">
                  {members.map((m, i) => (
                    <div key={m.id} className={`flex items-center justify-between px-4 py-3 ${i < members.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">{(m.nickname ?? m.username ?? '?')[0].toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{m.nickname ?? m.username}</p>
                          <p className="text-xs text-gray-400">{m.phone ?? `@${m.username}`}</p>
                        </div>
                      </div>
                      <button onClick={() => setResetTarget(m)}
                        className="text-xs bg-violet-500 text-white px-3 py-1.5 rounded-full cursor-pointer hover:bg-violet-600 transition-colors whitespace-nowrap">
                        <i className="ri-shield-keyhole-line mr-1" />重置密码
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {members.length === 0 && memberSearch && !membersLoading && (
                <div className="text-center py-10 text-gray-400 text-sm">
                  <i className="ri-search-line text-2xl block mb-2" />未找到匹配的会员
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* 弹窗 */}
      {resetTarget && <ResetPayPwdModal member={resetTarget} onClose={() => setResetTarget(null)} />}

      {showAdminForm && (
        <AdminFormModal
          editTarget={editAdmin}
          onClose={() => { setShowAdminForm(false); setEditAdmin(null); }}
          onSuccess={() => { setShowAdminForm(false); setEditAdmin(null); loadAdminUsers(); }}
        />
      )}

      {deleteAdminTarget && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !deleteAdminLoading && setDeleteAdminTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6" style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }} onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-delete-bin-line text-rose-500 text-xl" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">确认删除管理员</h3>
                  <p className="text-xs text-gray-400">此操作不可撤销</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                确定要删除管理员 <span className="font-semibold text-gray-900">「{deleteAdminTarget.nickname}」</span>（@{deleteAdminTarget.username}）吗？删除后该账号将无法登录后台。
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteAdminTarget(null)} disabled={deleteAdminLoading} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50">取消</button>
                <button onClick={handleDeleteAdmin} disabled={deleteAdminLoading} className="flex-1 bg-rose-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 disabled:opacity-50">
                  {deleteAdminLoading ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />删除中...</span> : '确认删除'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
